import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Router } from 'wouter';
import { memoryLocation } from 'wouter/memory-location';
import { retryDelay, shouldRetry } from './api/retry.ts';
import { AppRoutes } from './AppRoutes.tsx';
import { onSessionEnded } from './auth/expiry.ts';
import { SESSION_QUERY_KEY, signedOut } from './auth/session.ts';
import { ActivityBar } from './components/ActivityBar.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { OfflineBanner } from './components/OfflineBanner.tsx';
import { ToastProvider, ToastRegion } from './components/Toast.tsx';
import { QUERY_CACHE_TIME_MS } from './constants.ts';
import { useSheetAwareLocation } from './hooks/use-sheet-history.ts';
import { useUnloadGuardWhileSaving } from './hooks/use-unload-guard.ts';
import type { SessionStatus } from '../shared/schemas/user.ts';

interface AppProps {
  /** Tests pass an in-memory path; the browser build uses the real history. */
  readonly initialPath?: string;
  readonly queryClient?: QueryClient;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay,
        refetchOnWindowFocus: false,
        // `always` rather than Query's default: with no connection a read
        // still runs, and `apiRequest` answers it from the offline copy
        // instead of leaving the page paused for ever. What is already in
        // memory is kept for a day so moving between pages on a site with
        // no signal costs nothing at all.
        networkMode: 'always',
        gcTime: QUERY_CACHE_TIME_MS,
      },
      mutations: { networkMode: 'always' },
    },
  });
}

/**
 * When a protected request finds the session gone, flip the cached session
 * to "not authenticated" so the gate sends the person to the login page.
 * A protected route only renders once the session query has an answer, so
 * there is always a previous value to update; an absent one is left alone
 * and the gate's own fetch decides.
 */
function useSignOutWhenSessionEnds(client: QueryClient): void {
  useEffect(
    () =>
      onSessionEnded(() => {
        client.setQueryData<SessionStatus>(SESSION_QUERY_KEY, (previous) =>
          previous === undefined ? undefined : signedOut(previous),
        );
      }),
    [client],
  );
}

/** App-wide behaviours that need the query client and draw nothing. */
function QueryEffects({ client }: { readonly client: QueryClient }) {
  useSignOutWhenSessionEnds(client);
  useUnloadGuardWhileSaving();
  return null;
}

export function App({ initialPath, queryClient }: AppProps) {
  const [client] = useState(() => queryClient ?? createQueryClient());
  const [memory] = useState(() =>
    initialPath === undefined ? undefined : memoryLocation({ path: initialPath }),
  );
  return (
    <QueryClientProvider client={client}>
      <ToastProvider>
        <ErrorBoundary>
          <QueryEffects client={client} />
          {/* One landmark for what the app says about itself, so none of it
              is page content outside a landmark. */}
          <section aria-label="App status">
            <ActivityBar />
            <OfflineBanner />
            <ToastRegion />
          </section>
          <Router
            {...(memory === undefined
              ? { hook: useSheetAwareLocation }
              : { hook: memory.hook, searchHook: memory.searchHook })}
          >
            <AppRoutes />
          </Router>
        </ErrorBoundary>
      </ToastProvider>
    </QueryClientProvider>
  );
}
